import type {ShadowAssignmentDocument} from '@cip/core/firebase';
import {
  computeReviewerRequirement,
  parseSectionWeekId,
  type ReviewerRequiredAssignment,
} from '@cip/core/section';

/** The assignment fields the per-week requirement reads. */
export type ShadowAssignmentRequirementFields = Pick<
  ShadowAssignmentDocument,
  'sectionWeek' | 'status' | 'source'
>;

/**
 * Reduces a reviewer's assignments to the ones that bear on the per-week
 * requirement, in the shape `computeReviewerRequirement` takes. Opt-in `extra`
 * assignments are dropped, as is any assignment whose week is not a valid
 * section week.
 *
 * @param assignments - The reviewer's shadow assignments, of any source.
 * @return The `required`-source assignments, one entry each.
 */
export function toReviewerRequiredAssignments(
  assignments: ShadowAssignmentRequirementFields[],
): ReviewerRequiredAssignment[] {
  return assignments
    .filter(assignment => assignment.source === 'required')
    .map(assignment => ({
      sectionWeek: parseSectionWeekId(assignment.sectionWeek),
      isSubmitted: assignment.status === 'submitted',
      isActive: assignment.status === 'claimed',
    }))
    .filter(
      (assignment): assignment is ReviewerRequiredAssignment =>
        assignment.sectionWeek !== undefined,
    );
}

/**
 * Decides whether a roadmap "Section Shadow" item is complete: the reviewer
 * has submitted the course's full number of required reviews for the item's
 * week. The item's week is judged on its own, whether or not the course lists
 * it as a guaranteed-coverage week.
 *
 * @param completionId - The item's completion id, which holds its 1-based
 *   section week. Anything that is not a valid section week is never complete.
 * @param assignments - The reviewer's shadow assignments, of any source.
 * @param requiredPerWeek - Required reviews per week. Omitted means the
 *   default `computeReviewerRequirement` applies.
 * @return True when the week's requirement is met.
 */
export function isSectionShadowItemComplete({
  completionId,
  assignments,
  requiredPerWeek,
}: {
  completionId: unknown;
  assignments: ShadowAssignmentRequirementFields[];
  requiredPerWeek?: number;
}): boolean {
  const itemWeek = parseSectionWeekId(completionId);
  if (itemWeek === undefined) return false;
  const {completedWeeks} = computeReviewerRequirement({
    guaranteedWeeks: [itemWeek],
    requiredAssignments: toReviewerRequiredAssignments(assignments),
    requiredPerWeek,
  });
  return completedWeeks.includes(itemWeek);
}
